import { useId, useState } from "react";
import Button from "@web-ui/components/Button";
import Chip from "@web-ui/components/Chip";
import Icon from "@web-ui/components/Icon";
import type { Step } from "./use-setup-guide";

/**
 * Shopify's setup guide: "2 of 5 done", collapsible, dismissible; each step says why and goes
 * straight to the page that does it (and each is also reachable from that page's empty state).
 */
export function SetupGuide({ steps, onDismiss }: { steps: Step[]; onDismiss: () => void }) {
  const done = steps.filter((s) => s.done).length;
  const complete = done === steps.length;
  // A finished guide folds to its one line; its steps stay a click away.
  const [open, setOpen] = useState(!complete);
  const listId = useId();
  const next = steps.find((s) => !s.done);

  return (
    <section className="card flex flex-col gap-3" aria-labelledby="setup">
      <div className="flex flex-wrap items-center gap-3">
        <div className="me-auto min-w-0 ps-card-inset py-1">
          <h2 id="setup" className="text-sub-title-1">{complete ? "Your app is set up" : "Setup guide"}</h2>
          <p className="text-body-2 text-t-secondary">{complete ? `All ${steps.length} setup steps done` : `${done} of ${steps.length} done`}</p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>Dismiss guide</Button>
        <Button type="button" variant="ghost" size="sm" isIconOnly icon="chevron" className={open ? "rotate-180" : ""} aria-expanded={open} aria-controls={listId} aria-label={open ? "Hide the steps" : "Show the steps"} onClick={() => setOpen(!open)} />
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-b-surface1" role="progressbar" aria-valuemin={0} aria-valuemax={steps.length} aria-valuenow={done} aria-label="Setup progress">
        <div className="h-full rounded-full bg-positive transition-[width] duration-(--motion-base)" style={{ width: `${(done / steps.length) * 100}%` }} />
      </div>
      <div className="motion-collapse" data-open={open} id={listId}>
        <ol inert={!open} className="flex flex-col">
          {steps.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 rounded-2xl px-3 py-3">
              <span className={`flex size-7 shrink-0 items-center justify-center rounded-full ${s.done ? "bg-positive" : "border-2 border-dashed border-s-highlight"}`} aria-hidden="true">
                {s.done ? <Icon name="check" size={4} className="fill-on-positive" /> : null}
              </span>
              <div className="min-w-0 grow basis-48">
                <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-body-2 ${s.done ? "text-t-secondary line-through" : "font-medium text-t-primary"}`}>
                  {s.title}<span className="sr-only">{s.done ? " (done)" : " (to do)"}</span>
                  {s.needed ? <Chip tone="warning">Needed before you can publish</Chip> : null}
                </div>
                {!s.done ? <div className="text-caption text-t-secondary">{s.body}</div> : null}
              </div>
              <Button as="a" size="sm" variant={s.done ? "ghost" : s === next ? "secondary" : "outline"} href={s.action.href}>{s.action.label}</Button>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
